import { Button, Col, Form, Input, Row, Select, Space, theme } from "antd";
import { useGetCommentsQuery } from "../../../api/plamo-api";
import { useCallback, useEffect, useMemo, useState } from "react";
import PlamoApiWithSaveNotifications from "../util/with-save-notification";
import { CheckOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { IconBanSolid } from "../../../icon";
import { useGetUsersLightQuery } from "../../../api/auth-api";
import dayjs from "dayjs";

const { 
  useCreateCommentWithSaveNotificationMutation, 
  useUpdateCommentWithSaveNotificationMutation,
  useDeleteCommentWithSaveNotificationMutation 
} = PlamoApiWithSaveNotifications;


export const CommentUsernameDate = ({ comment }) => {
  const { data: users } = useGetUsersLightQuery({}) as any;
  const usernameDisplay = useMemo(() => users?.find(u => u.id === comment.creationUserId)?.usernameDisplay || "Unbekannt", [users, comment.creationUserId])
  const dateDisplay = useMemo(() => dayjs(comment.creationTime + 'Z').format('DD.MM.YYYY HH:mm:ss'), [comment.creationTime])
  const modifiedDisplay = useMemo(() => comment.creationTime !== comment.updateTime ? ' (bearbeitet)' : '', [comment.creationTime, comment.updateTime])

  return <><strong>{ usernameDisplay }</strong> schrieb am <strong>{ dateDisplay }</strong>{ modifiedDisplay }</>
}

export const CommentField = ({ value, onChange, edit, onEdit, onFinishEdit, onCancelEdit, onDelete, readOnly }: any) => {
  const [editContent, setEditContent] = useState<string>();

  
  const lines = value?.text?.split("\n");
  return <div style={{ position: "relative", marginTop: "0.5em" }}>
    { !readOnly && 
    <div style={{ position: "absolute", right: 0, top: 0 }}>
      <Space>
        { edit && <>
          <Button type="primary" icon={<CheckOutlined />} onClick={() => { onChange({...value, text: editContent}); onFinishEdit() } } /><Button danger icon={<IconBanSolid />} onClick={onCancelEdit} />
          </>
        }
        { !edit && <>
          <Button icon={<EditOutlined onClick={() => { setEditContent(value?.text); onEdit(); } } />} /><Button danger icon={<DeleteOutlined />} onClick={onDelete} />
          </>
        }
      </Space>
    </div>
    }
    <div><CommentUsernameDate comment={value} />:</div>
    {
      edit && <Input.TextArea
        placeholder=""
        autoSize={{ minRows: 2 }}
        style={{ width: "100%", marginTop: "1em" }}
        value={editContent}
        onChange={evt => setEditContent(evt.target.value)}
      />
    }
    {
      !edit && <div>{ lines?.map(l => <>{l}<br/></>) }</div>
    }
    <hr/>
  </div>
}

function Comments({ objectId, objectType, noHeader }: any) {
  const [form] = Form.useForm();
  const { data } = useGetCommentsQuery({ f: JSON.stringify({ objectType: { c: "EQ", v: objectType }, objectId: { c: "EQ", v: objectId } }), st: JSON.stringify({ id: 'desc' }) }) as any
  const [createComment] = useCreateCommentWithSaveNotificationMutation();
  const [updateComment] = useUpdateCommentWithSaveNotificationMutation();
  const [deleteComment] = useDeleteCommentWithSaveNotificationMutation();
  const [editCommentIdx, setEditCommentIdx] = useState<number | null>(null);
  
  const { token } = theme.useToken();

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    padding: 24,
  };

  useEffect(() => {
    form.setFieldsValue({ items: [{}, ...(data?.items || [])]})
  }, [form, data])

  const onFinish = useCallback(async (values: any) => {
    const newComment = {
      objectType,
      objectId, 
      ...values.items[0]
    }
    //console.log("Values", values, newComment);
    createComment(newComment);
  }, [createComment, objectId, objectType]);

  const onUpdateComment = useCallback(async (commentIdx: number) => {
    const values = form.getFieldsValue();
    const newComment = values.items[commentIdx];
    updateComment(newComment);
  }, [form, updateComment])

  const onDeleteComment = useCallback(async (commentIdx: number) => {
    const values = form.getFieldsValue();
    const commentForDeletion = values.items[commentIdx];
    deleteComment(commentForDeletion);
  }, [form, deleteComment])

  return (<Form form={form} name="comments" style={formStyle} onFinish={onFinish}>
    <Form.List name="items">
      {(fields, { add, remove }, { errors }) => <>
        { !noHeader && <Row><h3>Notizen</h3></Row> }
        <Row>
          <Col span={24}>
            { fields[0] && 
              <>
              {
                objectType === 'customer' && <Form.Item name={[fields[0].name, "type"]} initialValue={"Aktuelles"} style={{ width: '20em'}}><Select options={[{ label: "Aktuelles", value: "Aktuelles" }, { label: "Allgemein", value: "Allgemein" }]} /></Form.Item>
              }
              <Form.Item 
                {...fields[0]}
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}
                name={[fields[0].name, "text"]}
              >
                <Input.TextArea
                  placeholder="Neue Notiz einfügen"
                  autoSize={{ minRows: 2 }}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              </>
            }
          </Col>
        </Row>
        <div style={{ textAlign: 'right' }}>
          <Space size="small">
            <Button type="primary" htmlType="submit">
              Speichern
            </Button>
          </Space>
        </div>
        {
          fields.filter((_, idx) => idx !== 0).map((field, idx) => <Row>
            <Col span={24}>
            <Form.Item 
                {...field}
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}
                name={[field.name]}
              >
                <CommentField 
                    edit={editCommentIdx === idx + 1} 
                    onEdit={() => setEditCommentIdx(idx + 1)} 
                    onFinishEdit={() => { onUpdateComment(idx + 1); setEditCommentIdx(null); } } 
                    onCancelEdit={() => setEditCommentIdx(null)}
                    onDelete={() => onDeleteComment(idx + 1) }
                />
              </Form.Item>
            </Col>
          </Row>)
        }
      </>}
    </Form.List>
  </Form>)
}

export default Comments;